import { infiniteQueryOptions, skipToken } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import {
  paginatedAssignedInstitutesSchema,
  paginatedUnassignedInstitutesSchema,
  type AssignedInstitutesParams,
  type AssignInstitutesPayload,
  type PaginatedAssignedInstitutes,
  type PaginatedUnassignedInstitutes,
  type RemoveInstitutesPayload,
  type UnassignedInstitutesParams,
} from '../types/userInstituteMapping.types';

export const userInstituteMappingQueryKeys = {
  all: () => ['user-institute-mapping'] as const,
  assignedLists: () => [...userInstituteMappingQueryKeys.all(), 'assigned-list'] as const,
  assignedListInfinite: (params: Omit<AssignedInstitutesParams, 'page'>) =>
    [...userInstituteMappingQueryKeys.assignedLists(), 'infinite', params] as const,
  assignedListDisabled: () =>
    [...userInstituteMappingQueryKeys.assignedLists(), 'disabled'] as const,
  unassignedLists: () => [...userInstituteMappingQueryKeys.all(), 'unassigned-list'] as const,
  unassignedListInfinite: (params: Omit<UnassignedInstitutesParams, 'page'>) =>
    [...userInstituteMappingQueryKeys.unassignedLists(), 'infinite', params] as const,
  unassignedListDisabled: () =>
    [...userInstituteMappingQueryKeys.unassignedLists(), 'disabled'] as const,
};

export const assignedInstitutesInfiniteQueryOptions = (
  params: Omit<AssignedInstitutesParams, 'page'> | null,
) =>
  infiniteQueryOptions({
    queryKey: params
      ? userInstituteMappingQueryKeys.assignedListInfinite(params)
      : userInstituteMappingQueryKeys.assignedListDisabled(),
    queryFn: params
      ? async ({ pageParam }): Promise<PaginatedAssignedInstitutes> => {
          const res = await apiClient.get(API.instituteMapping.assigned, {
            params: { ...params, page: pageParam },
          });
          return paginatedAssignedInstitutesSchema.parse(res.data);
        }
      : skipToken,
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const unassignedInstitutesInfiniteQueryOptions = (
  params: Omit<UnassignedInstitutesParams, 'page'> | null,
) =>
  infiniteQueryOptions({
    queryKey: params
      ? userInstituteMappingQueryKeys.unassignedListInfinite(params)
      : userInstituteMappingQueryKeys.unassignedListDisabled(),
    queryFn: params
      ? async ({ pageParam }): Promise<PaginatedUnassignedInstitutes> => {
          const res = await apiClient.get(API.instituteMapping.unassigned, {
            params: { ...params, page: pageParam },
          });
          return paginatedUnassignedInstitutesSchema.parse(res.data);
        }
      : skipToken,
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function assignInstitutes(payload: AssignInstitutesPayload): Promise<void> {
  await apiClient.post(API.instituteMapping.assign, payload);
}

export async function removeInstitutes(payload: RemoveInstitutesPayload): Promise<void> {
  await apiClient.delete(API.instituteMapping.remove, { data: payload });
}
